import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import masterApi from "../api/masterApi";
import ModulePageShell from "../components/common/ModulePageShell";
import EmptyState from "../components/common/EmptyState";
import LoadingState from "../components/common/LoadingState";
import { PERFORMANCE_QUERY_KEYS } from "../constants/performanceConstants";
import usePerformancePermissions from "../hooks/usePerformancePermissions";
import { buildReadableCode } from "../utils/performanceCode";

const GoalCyclesPage = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can, PERFORMANCE_PERMISSIONS } = usePerformancePermissions();
  const [showGuide, setShowGuide] = React.useState(false);
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const canReadGoalCycles = can(PERFORMANCE_PERMISSIONS.GOALS_READ);
  const canCreateGoalCycles = can(PERFORMANCE_PERMISSIONS.GOALS_CREATE);
  const canUpdateGoalCycles = can(PERFORMANCE_PERMISSIONS.GOALS_UPDATE);

  if (!canReadGoalCycles) {
    return (
      <ModulePageShell
        title="Masters - Goal Cycles"
        description="Configure cycle windows for goals and appraisals."
      >
        <EmptyState message="You do not have permission to view goal cycles." />
      </ModulePageShell>
    );
  }

  const listQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.goalCycles],
    queryFn: () => masterApi.listGoalCycles({ page: 1, limit: 50 }),
  });

  const createMutation = useMutation({
    mutationFn: masterApi.createGoalCycle,
    onSuccess: () => {
      toast({ title: "Goal cycle created", status: "success", duration: 2000 });
      queryClient.invalidateQueries({
        queryKey: [PERFORMANCE_QUERY_KEYS.goalCycles],
      });
      reset();
    },
    onError: (error) => {
      toast({
        title: "Failed to create goal cycle",
        description: error?.response?.data?.message || error.message,
        status: "error",
      });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ goalCycleId, status }) =>
      masterApi.updateGoalCycleStatus(goalCycleId, { status }),
    onSuccess: () => {
      toast({
        title: "Goal cycle status updated",
        status: "success",
        duration: 2000,
      });
      queryClient.invalidateQueries({
        queryKey: [PERFORMANCE_QUERY_KEYS.goalCycles],
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to update goal cycle status",
        description: error?.response?.data?.message || error.message,
        status: "error",
      });
    },
  });

  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      name: "",
      cycleType: "YEARLY",
      startDate: "",
      endDate: "",
      reviewStartDate: "",
      reviewEndDate: "",
      status: "ACTIVE",
      description: "",
      isDefault: false,
    },
  });

  const rows = listQuery.data?.data || [];

  return (
    <ModulePageShell
      title="Masters - Goal Cycles"
      description="Configure cycle windows for goals and appraisals."
      onRefresh={() => listQuery.refetch()}
      showGuide={showGuide}
      actions={
        <Button
          size="sm"
          variant="outline"
          colorScheme={showGuide ? "blue" : "gray"}
          onClick={() => setShowGuide((prev) => !prev)}
        >
          i User Manual
        </Button>
      }
    >
      {canCreateGoalCycles ? (
        <Box
          bg={bg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={4}
        >
          <form
            onSubmit={handleSubmit((values) =>
              createMutation.mutate({
                ...values,
                code: buildReadableCode("GC", values.name),
              }),
            )}
          >
            <VStack align="stretch" spacing={3}>
              <Text fontWeight="semibold">Create Goal Cycle</Text>
              <HStack>
                <FormControl isRequired>
                  <FormLabel>Name</FormLabel>
                  <Input {...register("name")} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Type</FormLabel>
                  <Select {...register("cycleType")}>
                    <option value="YEARLY">YEARLY</option>
                    <option value="HALF_YEARLY">HALF_YEARLY</option>
                    <option value="QUARTERLY">QUARTERLY</option>
                    <option value="MONTHLY">MONTHLY</option>
                  </Select>
                </FormControl>
              </HStack>
              <HStack>
                <FormControl isRequired>
                  <FormLabel>Start</FormLabel>
                  <Input type="date" {...register("startDate")} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>End</FormLabel>
                  <Input type="date" {...register("endDate")} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Review Start</FormLabel>
                  <Input type="date" {...register("reviewStartDate")} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Review End</FormLabel>
                  <Input type="date" {...register("reviewEndDate")} />
                </FormControl>
              </HStack>
              <HStack>
                <FormControl isRequired maxW="280px">
                  <FormLabel>Status</FormLabel>
                  <Select {...register("status")}>
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </Select>
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Input
                  {...register("description")}
                  placeholder="Enter cycle description"
                />
              </FormControl>
              <Button
                type="submit"
                colorScheme="blue"
                alignSelf="flex-start"
                isLoading={createMutation.isPending}
              >
                Create Goal Cycle
              </Button>
            </VStack>
          </form>
        </Box>
      ) : (
        <EmptyState message="You have read access to goal cycles, but cannot create goal cycles." />
      )}

      <Box
        bg={bg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={4}
      >
        {listQuery.isLoading ? (
          <LoadingState />
        ) : rows.length === 0 ? (
          <EmptyState message="No goal cycles found." />
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Start Date</Th>
                <Th>End Date</Th>
                <Th>Description</Th>
                <Th>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((item) => (
                <Tr key={item._id}>
                  <Td>{item.name}</Td>
                  <Td>
                    {item.startDate
                      ? new Date(item.startDate).toLocaleDateString()
                      : "-"}
                  </Td>
                  <Td>
                    {item.endDate
                      ? new Date(item.endDate).toLocaleDateString()
                      : "-"}
                  </Td>
                  <Td>{item.description || "-"}</Td>
                  <Td>
                    {canUpdateGoalCycles ? (
                      <Select
                        size="sm"
                        value={item.status || "ACTIVE"}
                        onChange={(event) =>
                          updateStatusMutation.mutate({
                            goalCycleId: item._id,
                            status: event.target.value,
                          })
                        }
                        isDisabled={
                          updateStatusMutation.isPending &&
                          updateStatusMutation.variables?.goalCycleId ===
                            item._id
                        }
                        maxW="160px"
                      >
                        <option value="ACTIVE">ACTIVE</option>
                        <option value="INACTIVE">INACTIVE</option>
                      </Select>
                    ) : (
                      item.status || "-"
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
    </ModulePageShell>
  );
};

export default GoalCyclesPage;
